import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';

import { media } from 'src/components/base';

import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

import { AddToListButton } from 'src/components/action-buttons/add-to-list';
import { LayoutMode } from 'src/components/layout-buttons/types';
import { ProductGridCard } from 'src/components/product-card-new';
import { AdvizedCarousel } from '../../../../../components/advized';
import { isRaw } from '../../../utils/products';
import { Ads } from '../../ads';
import { OffersGrid } from '../../offers-grid';
import { DealsContext } from '../deals-context';
import HighlightedSearchResult from '../highlighted-search-result';

import type { ActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';
import type { ExpertCategoryContent, Offer, Product, Raw } from 'src/types';
import type { AdsLayoutConfig } from '../../ads/types';
import type { ProductComparisonProps } from '../types';

import { breakpointNames } from 'src/components/base/styles/variables';
import { ITEMS } from 'src/areas/product-collection/components/offers-grid/styles';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

const IMAGES_PER_ROW_MOBILE = 2;

type ProductCardWrapperProps = {
  lazyLoadImage?: boolean;
  onClick?: (product: {
    id: string | number;
    name?: string;
    type?: string;
  }) => void;
  product: Product | Raw | Offer;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
} & ProductComparisonProps;

const ProductCardWrapper = ({
  deselectProductById,
  isSelectingProducts,
  lazyLoadImage = true,
  onClick,
  product,
  productsWithActiveCampaign,
  selectProductById,
  selectedProducts,
}: ProductCardWrapperProps) => {
  const { getDealDiffPercentage } = useContext(DealsContext);
  const isAuthenticated = useIsAuthenticated();

  let id!: number;
  let offerId = '';

  if (!isRaw(product)) {
    id = product.id;
  } else if (product.__typename === 'Offer') {
    offerId = product.offerId ?? '';
  } else if (product.__typename === 'Raw') {
    offerId = `${product.id}`;
  }

  const dealsDiffPercentage = productsWithActiveCampaign
    ? productsWithActiveCampaign[id]?.diffPercentage
    : getDealDiffPercentage(id);

  const onClickHandler = useCallback(
    () =>
      onClick &&
      onClick({
        id: id ?? offerId,
        name: product.name,
        type: product.__typename,
      }),
    [id, offerId, onClick, product.__typename, product.name]
  );

  const onSelect = useCallback(
    () => selectProductById && selectProductById(id, 'productCard'),
    [id, selectProductById]
  );

  const onDeselect = useCallback(
    () => deselectProductById && deselectProductById(id, 'productCard'),
    [id, deselectProductById]
  );

  const getPrice = () => {
    switch (product.__typename) {
      case 'Product':
        return product.priceSummary.regular;
      case 'Offer':
        return product.offerPrice.regular;
      case 'Raw':
        return product.price;
      default:
        return undefined;
    }
  };

  const disableUnfeaturedClickout = isRaw(product) && !product.store?.featured;

  return !isRaw(product) ? (
    <ProductGridCard
      id={product.id}
      brand={product.brand}
      diffPercentage={dealsDiffPercentage}
      highlightedName={
        product.highlight ? (
          <HighlightedSearchResult>{product.highlight}</HighlightedSearchResult>
        ) : undefined
      }
      imageSrc={product.media?.first || ''}
      shouldLazyLoadImage={lazyLoadImage}
      link={product.pathName}
      ActionArea={
        isAuthenticated &&
        !isSelectingProducts && (
          <AddToListButton productId={product.id} productName={product.name} />
        )
      }
      name={product.name}
      onClick={onClickHandler}
      onDeselect={onDeselect}
      onSelect={onSelect}
      price={product.priceSummary.regular}
      rank={product.popularity?.inCategory}
      rating={
        product.aggregatedRating?.score || product.userReviewSummary?.rating
      }
      reviewsCount={
        product.aggregatedRating?.count || product.userReviewSummary?.count
      }
      properties={product.properties?.nodes}
      selectable={isSelectingProducts}
      selected={
        selectedProducts ? selectedProducts.some(p => p.id === id) : false
      }
    />
  ) : (
    <ProductGridCard
      type="offer"
      name={product.name}
      price={getPrice()}
      store={product.store}
      onClick={onClickHandler}
      link={disableUnfeaturedClickout ? undefined : product.externalUri}
      imageSrc={
        product.__typename === 'Offer' ? product.media?.first : undefined
      }
    />
  );
};

type Props = {
  feedbackPrompt?: React.ReactNode | null;
  feedbackPromptPosition?: number;
  onClick?: (productId: string | number, name?: string, type?: string) => void;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
  products: (Product | Raw)[];
  expert?: ExpertCategoryContent | null;
  adslayoutConfig?: AdsLayoutConfig;
  hasFeedback?: boolean;
  isExtraWide?: boolean;
} & ProductComparisonProps;

export const GridLayout = ({
  feedbackPrompt,
  feedbackPromptPosition = 6,
  onClick,
  productsWithActiveCampaign,
  products,
  expert,
  adslayoutConfig,
  isExtraWide,
  hasFeedback,
  ...productComparisonProps
}: Props) => {
  return (
    <OffersGrid
      isExtraWide={isExtraWide}
      offers={products.map((product, index) => {
        return (
          <ProductCardWrapper
            key={
              !isRaw(product)
                ? product.id
                : `${product.name}-${index.toString()}`
            }
            onClick={(productData: {
              id: string | number;
              name?: string;
              type?: string;
            }) => {
              onClick?.(productData.id, productData.name, productData.type);
              if (!isRaw(product))
                trackInternalNavigationClick({
                  scope: InternalNavigationScope.GRID_LAYOUT_PRODUCT_CARD,
                  nextPage: product.pathName,
                  index,
                });
            }}
            product={product}
            productsWithActiveCampaign={productsWithActiveCampaign}
            // Lazy loading is disabled for the first row of images (two first for mobile)
            // to enable images to load before React is loaded. The goal of this strategy
            // is to improve our Lighthouse performance score, and specifically the
            // "Largest Contentful Paint" metric.
            lazyLoadImage={index >= IMAGES_PER_ROW_MOBILE * 2}
            {...productComparisonProps}
          />
        );
      })}
    >
      <Ads
        offersLength={products.length}
        layout={LayoutMode.GRID}
        layoutConfig={adslayoutConfig}
      />

      {expert && <AdvizedCarousel {...expert} grid />}

      {hasFeedback && (
        <FeedbackPrompt $position={feedbackPromptPosition}>
          {feedbackPrompt}
        </FeedbackPrompt>
      )}
    </OffersGrid>
  );
};

type FeedbackPromptProps = {
  $position: number;
};

// This should work because OffersGrid (src/areas/product-collection/components/offers-grid/styles) renders first and mutates ITEMS during that render
// Therefore, ITEMS here should match the items number used to render the offers grid
const FeedbackPrompt = styled.li<FeedbackPromptProps>`
  grid-row: ${p => p.$position};
  ${breakpointNames.map(
    bp => media[`${bp}Only`]`grid-column: 1 / span ${ITEMS[bp]}`
  )}
`;
